import EmptyState from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import ValidationErrors from '@/components/validation-errors';
import type { Note } from '@/types/note';
import {
  project_note_path,
  project_note_promotion_path,
  project_notes_path,
} from '@/rails/routes';
import { Form, router } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { useState } from 'react';

interface Props {
  projectId: string;
  notes: Note[];
  embedded?: boolean;
}

export default function NotesCard({ projectId, notes, embedded = false }: Props) {
  const [adding, setAdding] = useState(false);

  const addForm = adding ? (
    <Form
      method="post"
      action={project_notes_path(projectId)}
      className="bg-muted/40 space-y-3 rounded-lg border p-3"
      onSuccess={() => setAdding(false)}
    >
      {({ errors, processing }) => (
        <>
          <Textarea
            id="note_body"
            name="note[body]"
            placeholder="Context for this project"
            rows={3}
            required
            autoFocus
          />
          <ValidationErrors errors={errors.body} />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={processing}>
              Add
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setAdding(false)}
              disabled={processing}
            >
              Cancel
            </Button>
          </div>
        </>
      )}
    </Form>
  ) : null;

  const list =
    notes.length === 0 && !adding ? (
      <EmptyState description="No notes yet." className="py-2" />
    ) : notes.length > 0 ? (
      <ul className="divide-y">
        {notes.map((note) => (
          <li key={note.id} className="space-y-2 py-3">
            <p className="text-sm whitespace-pre-wrap">{note.body}</p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => router.post(project_note_promotion_path(projectId, note.id))}
              >
                Promote to todo
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => router.delete(project_note_path(projectId, note.id))}
              >
                Remove
              </Button>
            </div>
          </li>
        ))}
      </ul>
    ) : null;

  const body = (
    <div className="space-y-3">
      {addForm}
      {list}
    </div>
  );

  if (embedded) {
    return (
      <div className="space-y-3">
        <div className="flex justify-end">
          {!adding ? (
            <Button type="button" size="sm" variant="outline" onClick={() => setAdding(true)}>
              <Plus className="size-3.5" />
              Add note
            </Button>
          ) : null}
        </div>
        {body}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
        <div className="space-y-1">
          <CardTitle className="text-base font-medium">Notes</CardTitle>
          <CardDescription>
            Project context. Promote to a todo when it becomes action.
          </CardDescription>
        </div>
        {!adding ? (
          <Button type="button" size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="size-3.5" />
            Add note
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
